import styled, { keyframes } from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';

// Animações
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const borderGlow = keyframes`
  0% { border-color: rgba(0, 200, 255, 0.3); }
  50% { border-color: rgba(0, 255, 255, 0.7); }
  100% { border-color: rgba(0, 200, 255, 0.3); }
`;

const pulseHighlight = keyframes`
  0% { background-color: rgba(64, 128, 255, 0.05); }
  50% { background-color: rgba(64, 128, 255, 0.15); }
  100% { background-color: rgba(64, 128, 255, 0.05); }
`;

const rotate = keyframes`
  0% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
  100% { transform: rotate(-2deg); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
  50% { box-shadow: 0 0 20px rgba(64, 128, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.4); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
`;

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

const iconPulse = keyframes`
  0% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(64, 128, 255, 0.3)); }
  50% { transform: scale(1.1); filter: drop-shadow(0 0 8px rgba(64, 128, 255, 0.7)); }
  100% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(64, 128, 255, 0.3)); }
`;

// Container principal
export const CheckoutContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #0A1930 0%, #0F2447 100%);
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 10% 10%, rgba(0, 100, 255, 0.05) 0%, transparent 20%),
      radial-gradient(circle at 90% 20%, rgba(64, 128, 255, 0.05) 0%, transparent 20%),
      radial-gradient(circle at 50% 50%, rgba(0, 200, 255, 0.03) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(0, 150, 255, 0.05) 0%, transparent 20%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(135deg, rgba(64, 128, 255, 0.05) 0%, transparent 50%),
      linear-gradient(225deg, rgba(0, 200, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

// Conteúdo do checkout
export const CheckoutContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: rgba(10, 20, 40, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 
              0 0 0 1px rgba(64, 128, 255, 0.1),
              0 0 20px rgba(0, 200, 255, 0.1);
  position: relative;
  z-index: 1;
  border: 1px solid rgba(64, 128, 255, 0.2);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, 
      rgba(0, 0, 0, 0), 
      rgba(64, 128, 255, 0.8), 
      rgba(0, 0, 0, 0)
    );
    z-index: 2;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(135deg, rgba(64, 128, 255, 0.05) 0%, transparent 50%),
      linear-gradient(225deg, rgba(0, 200, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

// Título do checkout
export const CheckoutTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #00BFFF;
  margin-bottom: 2rem;
  text-align: center;
  letter-spacing: 3px;
  position: relative;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.5);
  
  .tech-text {
    background: linear-gradient(to right, #64B6FF, #00FFFF, #00BFFF);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-size: 200% auto;
    animation: ${gradientMove} 5s linear infinite;
  }
  
  .tech-line {
    height: 2px;
    width: 100px;
    background: linear-gradient(to right, 
      rgba(0, 0, 0, 0), 
      #00BFFF, 
      rgba(0, 0, 0, 0)
    );
    margin: 0.5rem auto;
    position: relative;
    
    &::before, &::after {
      content: '';
      position: absolute;
      top: -3px;
      width: 8px;
      height: 8px;
      background: #00BFFF;
      border-radius: 50%;
      box-shadow: 0 0 10px rgba(64, 128, 255, 0.8);
    }
    
    &::before {
      left: 30%;
    }
    
    &::after {
      right: 30%;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Informações do produto
export const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 1.5rem;
  background: rgba(10, 25, 47, 0.3);
  border-radius: 15px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 30%, rgba(0, 255, 255, 0.05) 0%, rgba(0, 0, 0, 0) 20%),
      radial-gradient(circle at 80% 70%, rgba(0, 255, 170, 0.05) 0%, rgba(0, 0, 0, 0) 20%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(0, 255, 255, 0.03) 0%,
      rgba(0, 0, 0, 0) 70%
    );
    opacity: 0.5;
    pointer-events: none;
  }
`;

// Destaque de preço
export const PriceHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-align: center;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  
  span {
    font-size: 1.2rem;
    opacity: 0.8;
    margin-left: 5px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    span {
      font-size: 1rem;
    }
  }
`;

// Tag de bônus
export const BonusTag = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #000;
  background: linear-gradient(to right, #64B6FF, #00FFFF);
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  margin: 0.5rem auto;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 1px;
  box-shadow: 0 0 15px rgba(64, 128, 255, 0.6);
  animation: ${rotate} 3s ease-in-out infinite;
  transform-origin: center;
  border: 1px solid rgba(64, 128, 255, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// Destaque de crédito
export const CreditHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #00BFFF;
  margin: 1rem 0 1.5rem;
  text-align: center;
  padding: 1rem 1.5rem;
  background: rgba(64, 128, 255, 0.15);
  border-radius: 20px;
  border: 2px solid rgba(64, 128, 255, 0.3);
  animation: ${pulseHighlight} 3s infinite ease-in-out;
  text-shadow: 0 0 5px rgba(64, 128, 255, 0.5);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(64, 128, 255, 0.2) 0%,
      rgba(64, 128, 255, 0) 60%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0.8rem 1rem;
  }
`;

// Requisitos de plataforma
export const PlatformRequirements = styled.div`
  width: 100%;
  margin: 1rem 0;
  padding: 1.5rem;
  background: rgba(10, 25, 47, 0.5);
  border-radius: 15px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(to right, 
      rgba(0, 0, 0, 0), 
      rgba(64, 128, 255, 0.8), 
      rgba(0, 0, 0, 0)
    );
    animation: ${scanline} 4s infinite linear;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, rgba(64, 128, 255, 0.03) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(64, 128, 255, 0.03) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(64, 128, 255, 0.03) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(64, 128, 255, 0.03) 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    pointer-events: none;
    opacity: 0.5;
  }
`;

// Título da plataforma
export const PlatformTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1.2rem;
  text-align: center;
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
  width: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 50%;
    height: 1px;
    background: linear-gradient(to right, 
      rgba(0, 0, 0, 0), 
      rgba(64, 128, 255, 0.8), 
      rgba(0, 0, 0, 0)
    );
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Item da plataforma
export const PlatformItem = styled.div`
  margin: 1.2rem 0 0;
  padding: 0.8rem 0;
  text-align: center;
  position: relative;
`;

// Destaque especial
export const SpecialHighlight = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  padding: 1rem 1.5rem;
  background: rgba(64, 128, 255, 0.15);
  border-radius: 15px;
  border: 2px solid rgba(64, 128, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulseHighlight} 3s infinite ease-in-out;
  position: relative;
  overflow: hidden;
  
  svg {
    color: #00BFFF;
    font-size: 1.3rem;
    filter: drop-shadow(0 0 5px rgba(64, 128, 255, 0.7));
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      rgba(64, 128, 255, 0.2) 0%,
      rgba(0, 0, 0, 0) 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1rem;
  }
`;

// Grid de serviços
export const ServicesGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  gap: 1.5rem;
  margin: 1.5rem 0;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.2rem;
    align-items: center;
  }
`;

// Card de serviço
export const ServiceCard = styled.div`
  background: rgba(10, 25, 47, 0.7);
  border-radius: 15px;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(64, 128, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(64, 128, 255, 0.5);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(64, 128, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 50px;
    height: 50px;
    background: radial-gradient(circle at bottom right, rgba(64, 128, 255, 0.1), transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem 0.8rem;
  }
`;

// Ícone de serviço
export const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.color || '#00A67E'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.8rem;
  color: white;
  box-shadow: 0 0 15px ${props => props.color || '#00A67E'}80;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border-radius: 50%;
    border: 1px solid ${props => props.color || '#00A67E'}80;
    opacity: 0.5;
    animation: ${pulseHighlight} 2s infinite;
  }
  
  svg {
    filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
  }
`;

// Nome do serviço
export const ServiceName = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
  letter-spacing: 1px;
`;

// Preço do serviço
export const ServicePrice = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.3rem 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  margin-top: 0.5rem;
`;

// Container para o botão do Stripe
export const StripeBuyButtonContainer = styled.div`
  margin: 1.5rem auto;
  max-width: 100%;
  position: relative;
  padding: 0.5rem;
  border-radius: 10px;
  background: rgba(10, 25, 47, 0.5);
  border: 1px solid rgba(64, 128, 255, 0.3);
  animation: ${glowPulse} 4s infinite;
  
  stripe-buy-button {
    --button-theme: dark;
    --button-height: 60px;
    --button-font-family: 'Orbitron', sans-serif;
    --button-font-weight: 600;
    --button-letter-spacing: 1px;
    --button-border-radius: 8px;
    --button-icon-color: #00FFFF;
    --button-icon-transition: transform 0.3s ease;
    
    &:hover {
      --button-icon-transform: scale(1.1);
    }
  }
`;

// Botão de voltar
export const BackButton = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  padding: 0.7rem 1.5rem;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;
  
  &:hover {
    color: white;
    border-color: rgba(64, 128, 255, 0.5);
    box-shadow: 0 0 10px rgba(64, 128, 255, 0.3);
    transform: translateY(-2px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent,
      rgba(64, 128, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

// Ícones
export const CheckIcon = styled(FaCheck)`
  color: #00BFFF;
  font-size: 1rem;
`;

export const CrossIcon = styled(FaTimes)`
  color: #FF5555;
  font-size: 1rem;
`;

// Container para a logo
export const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
  
  img {
    width: 120px;
    height: auto;
    filter: drop-shadow(0 0 15px rgba(64, 128, 255, 0.7));
    animation: ${pulseHighlight} 4s infinite ease-in-out;
    transition: transform 0.3s ease;
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    
    &:hover {
      transform: scale(1.05);
    }
  }
  
  .tech-line {
    height: 2px;
    width: 100px;
    background: linear-gradient(to right, rgba(0, 0, 0, 0), #00BFFF, rgba(0, 0, 0, 0));
    margin: 1rem auto 0;
    position: relative;
    
    &::before, &::after {
      content: '';
      position: absolute;
      top: -3px;
      width: 8px;
      height: 8px;
      background: #00BFFF;
      border-radius: 50%;
      box-shadow: 0 0 10px rgba(64, 128, 255, 0.8);
    }
    
    &::before {
      left: 30%;
    }
    
    &::after {
      right: 30%;
    }
  }
  
  @media (max-width: 768px) {
    img {
      width: 100px;
    }
  }
`;

// Container personalizado para o Stripe
export const StripeCustomContainer = styled.div`
  margin: 2rem auto 1rem;
  max-width: 400px;
  width: 100%;
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(64, 128, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(64, 128, 255, 0.2);
  animation: ${glowPulse} 4s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, #64B6FF, #00BFFF);
  }
`;

export const StripeProductName = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.5);
`;

export const StripePrice = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0.5rem 0 0;
  background: linear-gradient(to right, #64B6FF, #00FFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.5);
`;

export const StripeMonthlyText = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
`;

export const StripeCustomButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #0070f3, #00BFFF);
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 1rem 0;
  box-shadow: 0 0 15px rgba(0, 112, 243, 0.3);
  
  &:hover {
    background: linear-gradient(to right, #0051cc, #0099cc);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 112, 243, 0.5);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const StripePaymentMethods = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1rem;
  width: 100%;
  
  .payment-icons {
    display: flex;
    justify-content: center;
    gap: 0.8rem;
    margin-top: 0.8rem;
    
    img {
      height: 24px;
      width: auto;
      filter: brightness(0.9) contrast(1.1);
    }
  }
`;

// Nota adicional sobre o custo por lead
export const LeadCostNote = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  margin-top: -0.5rem;
  margin-bottom: 1rem;
  font-style: italic;
  text-align: center;
  letter-spacing: 0.5px;
`;
